/**
 * Plan of Record — Executive Dashboard. The instrument panel: the one screen a
 * founder or exec opens in a meeting.
 *
 * Layout:
 *   Header → KPI Grid →
 *   [ Committed Initiatives | Active Signal Events ]
 *   [ Business Proof Points | Top Risks            ]
 *   [                       | Executive Asks       ]
 *   Footer
 *
 * Signal Condition Band (A/B/C/D) is intentionally not shown as a standing
 * present-state control. Canon 23 §4: failure modes are retrospective verdicts,
 * not live self-classification. Reintroduce later as a review/verdict surface.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Settings2, ShieldCheck } from 'lucide-react';
import type { AssembledPor, PorStatus } from '../../types/plan-of-record';
import {
  canRatifyIndependently,
  listPorSnapshots,
  reopenPorForAmendment,
  setPorStatus,
  updatePorRecord,
} from '../../lib/planOfRecordService';
import { usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';
import { RatifyPorModal } from '../../components/plan-of-record/RatifyPorModal';
import { KpiGrid } from '../../components/plan-of-record/KpiGrid';
import { PorProgressSummary } from '../../components/plan-of-record/PorProgressSummary';
import { CommitmentStateRollup } from '../../components/plan-of-record/CommitmentStateRollup';
import { CommitmentsPanel } from '../../components/plan-of-record/CommitmentsPanel';
import { SignalEventsPanel } from '../../components/plan-of-record/SignalEventsPanel';
import { RisksPanel } from '../../components/plan-of-record/RisksPanel';
import { ExecutiveAsksPanel } from '../../components/plan-of-record/ExecutiveAsksPanel';
import { ProofPointsPanel } from '../../components/plan-of-record/ProofPointsPanel';
import CycleConditionHealthPanel from '../../components/prioritization/conditions/CycleConditionHealthPanel';
import { useConditionsEnabledForWorkspace } from '../../hooks/useConditionsEnabled';
import {
  GhostButton,
  PorModal,
  PorStatusPill,
  PrimaryButton,
  RationaleModal,
  formatDate,
  relativeDays,
} from '../../components/plan-of-record/porUi';
import { RATIFY_POR_LABEL } from '../../lib/planning/vocabulary';

// Legal plain status transitions (deployed set_por_status state machine),
// MINUS the edges owned by governed actions: ratification (→ approved) goes
// through ratify_por, and reopening (approved → in_review) goes through
// reopen_por_for_amendment — both write the official-record machinery that a
// bare status flip would skip.
//
// approved → superseded is deliberately NOT offered here (removed 2026-08-21):
// a record can only be superseded BY a successor — which happens when the next
// version is ratified — never by a bare status flip that leaves it crossed out
// with nothing replacing it and no way back.
const PLAIN_TRANSITIONS: Partial<Record<PorStatus, PorStatus[]>> = {
  draft: ['in_review'],
  in_review: ['draft'],
};

export default function PorDashboard({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const { record } = assembled;
  const { porHref } = usePlanInstrumentNav();
  // Record-scoped gate: a Plan of Record opened by URL can belong to a
  // different workspace than the session's (the 2026-08-21 moduleGate defect).
  const conditionsEnabled = useConditionsEnabledForWorkspace(record.workspace_id);
  const [statusTarget, setStatusTarget] = useState<PorStatus | null>(null);
  const [managing, setManaging] = useState(false);
  const [ratifying, setRatifying] = useState(false);
  /** Version just ratified — confirms the act and links to the official record it wrote. */
  const [ratifiedVersion, setRatifiedVersion] = useState<number | null>(null);
  const [reopening, setReopening] = useState(false);
  // One ceremony per record (migration 678): a cycle-linked narrative is
  // frozen by the plan's authorize_por, so the standalone Ratify action only
  // exists on cycle-less org-level records.
  const canRatify = canRatifyIndependently(record);
  const ratifiesWithPlan =
    (record.status === 'draft' || record.status === 'in_review') && record.plan_cycle_id != null;
  const isRatified = record.status === 'approved';

  // The official record is a FROZEN ratification snapshot (migration 648) —
  // the stamp renders from what actually exists, never from status alone. An
  // approved record with no snapshot must not advertise a record that isn't
  // there (that was the "Official record v1.0" → "No official record yet"
  // dead-end). undefined = loading, null = none on file.
  const [officialVersion, setOfficialVersion] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    void listPorSnapshots(assembled.id).then(({ data }) => {
      if (cancelled) return;
      setOfficialVersion(data.length > 0 ? data[0].version : null);
    });
    return () => { cancelled = true; };
  }, [assembled.id, record.status, record.version]);

  return (
    <div className="space-y-5">
      {ratifiedVersion != null && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 flex-wrap p-3 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]"
        >
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 flex-shrink-0" />
            Ratified — record v{ratifiedVersion} is on file.
          </span>
          <Link to={porHref(assembled.id, 'official')} className="font-medium underline whitespace-nowrap">
            Next: open the ratified record
          </Link>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--text-ter)]">
            Executive Dashboard
          </div>
          <h1 className="text-2xl font-semibold text-[var(--text-pri)] mt-0.5">{record.title}</h1>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-xs font-mono text-[var(--text-ter)]">v{record.version}</span>
            <PorStatusPill status={record.status} />
            {(record.horizon_short || record.horizon_long) && (
              <span className="text-xs text-[var(--text-ter)]">
                Horizon · {record.horizon_short ?? '—'} / {record.horizon_long ?? '—'}
              </span>
            )}
            <PhaseStrip current={record.current_phase} total={record.total_phases} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Audience-shaped read views — each one a shareable URL */}
          <span className="hidden sm:flex items-center gap-1 mr-1 text-xs text-[var(--text-ter)]">
            <span className="font-mono uppercase tracking-wider text-[10px]">Share</span>
            <Link
              to={porHref(assembled.id, 'synopsis')}
              className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 px-1"
            >
              Synopsis
            </Link>
            <span aria-hidden>·</span>
            <Link
              to={porHref(assembled.id, 'sponsor')}
              className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 px-1"
            >
              Sponsor brief
            </Link>
          </span>
          {/* One action hierarchy: primary (Ratify) > the official-record
              stamp (emerald — only when a frozen snapshot exists) > uniform
              ghost affordances (Reopen, Change status, Manage), all sharing
              the GhostButton size and weight. */}
          {canRatify && (
            <PrimaryButton onClick={() => setRatifying(true)}>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> {RATIFY_POR_LABEL}
              </span>
            </PrimaryButton>
          )}
          {ratifiesWithPlan && (
            <Link
              to={`/planning/plans/${record.plan_cycle_id}/governance`}
              title="This record is linked to a plan cycle: it is ratified — content frozen, ratified record written with plan-version provenance — when that plan's POR is authorized."
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 border border-primary-200 text-primary-700 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-400 dark:hover:bg-primary-950/30 transition-colors"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Ratifies with plan authorization
            </Link>
          )}
          {isRatified && officialVersion != null && (
            <Link
              to={porHref(assembled.id, 'official')}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 border border-[var(--green)] text-[var(--green)] hover:bg-[var(--green-dim)] dark:border-[var(--green)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--green)]/30 transition-colors"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Ratified record v{officialVersion}
            </Link>
          )}
          {isRatified && officialVersion === null && (
            <span
              className="text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)]"
              title="This record is approved but no ratification snapshot is on file — reopen for amendment and ratify to create one."
            >
              No ratified record on file
            </span>
          )}
          {isRatified && (
            <GhostButton onClick={() => setReopening(true)}>Reopen for amendment</GhostButton>
          )}
          {(PLAIN_TRANSITIONS[record.status] ?? []).length > 0 && (
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) setStatusTarget(e.target.value as PorStatus);
              }}
              className="text-sm font-medium px-3 py-1.5 border border-[var(--rule)] text-[var(--text-sec)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              <option value="">Change status…</option>
              {(PLAIN_TRANSITIONS[record.status] ?? []).map((s) => (
                <option key={s} value={s}>
                  {s.replace('_', ' ')}
                </option>
              ))}
            </select>
          )}
          <GhostButton onClick={() => setManaging(true)}>
            <span className="inline-flex items-center gap-1">
              <Settings2 className="h-3.5 w-3.5" /> Manage
            </span>
          </GhostButton>
        </div>
      </header>

      {/* ── KPI Grid ────────────────────────────────────────────────────── */}
      <KpiGrid assembled={assembled} refresh={refresh} />

      {/* ── Progress rollup — completion + day counter ──────────────────── */}
      <PorProgressSummary assembled={assembled} />

      {/* ── Resourcing rollup — announced vs committed (gated, D1) ───────── */}
      <CommitmentStateRollup assembled={assembled} />

      {/* ── Lower two-column grid ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="space-y-5">
          <CommitmentsPanel assembled={assembled} refresh={refresh} />
          <ProofPointsPanel assembled={assembled} refresh={refresh} />
        </div>
        <div className="space-y-5">
          {conditionsEnabled && record.plan_cycle_id && (
            <CycleConditionHealthPanel planCycleId={record.plan_cycle_id} />
          )}
          <SignalEventsPanel assembled={assembled} refresh={refresh} />
          <RisksPanel assembled={assembled} refresh={refresh} />
          <ExecutiveAsksPanel assembled={assembled} refresh={refresh} />
        </div>
      </div>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-[var(--rule)] pt-4 text-xs text-[var(--text-ter)]">
        <span>Version {record.version}</span>
        <span>Last updated {formatDate(record.last_updated)}</span>
        <span>
          Next review {formatDate(record.next_review)}
          {record.next_review ? ` · ${relativeDays(record.next_review)}` : ''}
        </span>
        <span>Status · {record.status.replace('_', ' ')}</span>
        <span className="ml-auto font-mono">
          {assembled.governance_events.length} governance event
          {assembled.governance_events.length === 1 ? '' : 's'} on record
        </span>
      </footer>

      {statusTarget && (
        <RationaleModal
          title={`Change status → ${statusTarget.replace('_', ' ')}`}
          subtitle="A status change is recorded in the immutable Change Log."
          confirmLabel="Change status"
          onClose={() => setStatusTarget(null)}
          onConfirm={async (rationale) => {
            const { error } = await setPorStatus(assembled.id, statusTarget, rationale);
            if (error) throw new Error(error);
            setStatusTarget(null);
            await refresh();
          }}
        />
      )}

      {managing && (
        <ManageRecordModal
          assembled={assembled}
          onClose={() => setManaging(false)}
          onDone={async () => {
            setManaging(false);
            await refresh();
          }}
        />
      )}

      {ratifying && (
        <RatifyPorModal
          porId={assembled.id}
          onClose={() => setRatifying(false)}
          onRatified={async (version) => {
            setRatifying(false);
            setRatifiedVersion(version);
            await refresh();
          }}
        />
      )}

      {reopening && (
        <RationaleModal
          title="Reopen for amendment"
          subtitle={`Ratified record v${officialVersion ?? record.version} remains authoritative until ${
            record.plan_cycle_id != null
              ? "the plan's next POR authorization freezes the amendment as the next version."
              : 'you ratify again — the amendment becomes the next version.'
          }`}
          confirmLabel="Reopen"
          onClose={() => setReopening(false)}
          onConfirm={async (rationale) => {
            const { error } = await reopenPorForAmendment(assembled.id, rationale);
            if (error) throw new Error(error);
            setReopening(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/** Compact visual indicator of the current phase within the macro arc. */
function PhaseStrip({ current, total }: { current: number; total: number }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={`Phase ${current} of ${total}`}>
      <span className="text-[11px] font-mono text-[var(--text-ter)]">
        Phase {current}/{total}
      </span>
      <span className="inline-flex gap-0.5">
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-3  ${
              i + 1 < current
                ? 'bg-primary-400'
                : i + 1 === current
                  ? 'bg-electric-mustard-600'
                  : 'bg-[var(--hover-tint)]'
            }`}
          />
        ))}
      </span>
    </span>
  );
}

function ManageRecordModal({
  assembled,
  onClose,
  onDone,
}: {
  assembled: AssembledPor;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { record } = assembled;
  const [title, setTitle] = useState(record.title);
  const [version, setVersion] = useState(record.version);
  const [horizonShort, setHorizonShort] = useState(record.horizon_short ?? '');
  const [horizonLong, setHorizonLong] = useState(record.horizon_long ?? '');
  const [phase, setPhase] = useState(String(record.current_phase));
  const [totalPhases, setTotalPhases] = useState(String(record.total_phases));
  const [nextReview, setNextReview] = useState(
    record.next_review ? record.next_review.slice(0, 10) : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    const cp = Math.max(1, Number(phase) || 1);
    const tp = Math.max(cp, Number(totalPhases) || cp);
    setBusy(true);
    setError(null);
    const { error: err } = await updatePorRecord(assembled.id, {
      title: title.trim(),
      version: version.trim() || record.version,
      horizon_short: horizonShort.trim() || null,
      horizon_long: horizonLong.trim() || null,
      current_phase: cp,
      total_phases: tp,
      next_review: nextReview ? new Date(nextReview).toISOString() : null,
    });
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <PorModal
      title="Manage Plan of Record"
      subtitle="Edits here update the record. Status and condition changes have their own governed flows."
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={label}>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={field} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={label}>Version</label>
            <input value={version} onChange={(e) => setVersion(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label}>Phase</label>
            <input
              type="number"
              min={1}
              value={phase}
              onChange={(e) => setPhase(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className={label}>Total phases</label>
            <input
              type="number"
              min={1}
              value={totalPhases}
              onChange={(e) => setTotalPhases(e.target.value)}
              className={field}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={label}>Short horizon</label>
            <input value={horizonShort} onChange={(e) => setHorizonShort(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label}>Long horizon</label>
            <input value={horizonLong} onChange={(e) => setHorizonLong(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label}>Next review</label>
            <input
              type="date"
              value={nextReview}
              onChange={(e) => setNextReview(e.target.value)}
              className={field}
            />
          </div>
        </div>
        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
